import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import Select from 'react-select';

const WarehouseEdit = () => {
  const navigate = useNavigate();
  const { pointindex } = useParams();
  const { id } = useParams();
  const [branch, setBranch] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [warehousename, setwarehousename] = useState("");
  const [warehousetype, setwarehousetype] = useState('');
  const [contactperson, setcontactperson] = useState("");
  const [email, setEmail] = useState("");
  const [mobileno, setmobileno] = useState("");
  const [address, setaddress] = useState("");
  const [zipcode, setzipcode] = useState("");
  const [country, setcountry] = useState(null);
  const [state, setstate] = useState(null);
  const [city, setcity] = useState(null);
  const [countryOptions, setcountryOptions] = useState([]);
  const [stateOptions, setstateOptions] = useState([]);
  const [cityOptions, setcityOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statecode, setstatecode] = useState("");

  // Validation states
  const [errors, setErrors] = useState({
    warehousename: "",
    warehousetype: "",
    branch: "",
    contactperson: "",
    email: "",
    mobileno: ""
  });

  const [touched, setTouched] = useState({
    warehousename: false,
    warehousetype: false,
    branch: false,
    contactperson: false,
    email: false,
    mobileno: false
  });

  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}warehouse/findById/${id}`, {
          headers: authHeader(),
        });
        const branchData = response.data;
        setstatecode(branchData.statecode);
        setwarehousename(branchData.warehousename);
        setwarehousetype(branchData.warehousetype);
        setBranch({ value: branchData.branch.id, label: branchData.branch.branchname });
        setmobileno(branchData.mobileno);
        setEmail(branchData.email);
        setaddress(branchData.address);
        setzipcode(branchData.zipcode);
        setcontactperson(branchData.contactperson);

        const countryResponse = await axios.get(BASE_URL + 'country/findAllCountry', {
          headers: authHeader(),
        });
        setcountryOptions(countryResponse.data.map(country => ({
          value: country.id,
          label: country.countryname
        })));

        if (branchData.country) {
          const stateResponse = await axios.get(
            BASE_URL + `state/stateByCountry/${branchData.country.id}`,
            { headers: authHeader() }
          );
          setstateOptions(stateResponse.data.map(state => ({
            value: state.id,
            label: state.statename,
            code: state.statecode
          })));
        }

        if (branchData.state) {
          const cityResponse = await axios.get(
            BASE_URL + `city/cityByStateId/${branchData.state.id}`,
            { headers: authHeader() }
          );
          setcityOptions(cityResponse.data.map(city => ({
            value: city.id,
            label: city.cityname
          })));
        }

        setcountry(branchData.country ? {
          value: branchData.country.id,
          label: branchData.country.countryname
        } : null);

        setstate(branchData.state ? {
          value: branchData.state.id,
          label: branchData.state.statename
        } : null);

        setcity(branchData.city ? {
          value: branchData.city.id,
          label: branchData.city.cityname
        } : null);

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch warehouse data. Please try again.',
        });
      }
    };

    fetchWarehouseData();
    getBranchData();
  }, [id]);

  const getBranchData = async () => {
    try {
      const response = await axios.get(BASE_URL + 'branch/findAllBranch', {
        headers: authHeader(),
      });
      setBranchOptions(response.data.map(branch => ({
        value: branch.id,
        label: branch.branchname
      })));
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch branch. Please try again.',
      });
    }
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "warehousename":
        if (!value.trim()) error = "Warehouse Name is required";
        break;
      case "warehousetype":
        if (!value.trim()) error = "Warehouse Type is required";
        break;
      case "branch":
        if (!value) error = "Branch is required";
        break;
      case "contactperson":
        if (value && !/^[a-zA-Z ]+$/.test(value.trim())) {
          error = "Contact Person should contain only letters";
        }
        break;
      case "email":
        if (value && !/^\S+@\S+\.\S+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "mobileno":
        if (value && !/^[0-9]{10}$/.test(value)) {
          error = "Please enter a valid 10-digit phone number";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target || {};
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "warehousename":
        setwarehousename(value);
        break;
      case "warehousetype":
        setwarehousetype(value);
        break;
      case "contactperson":
        setcontactperson(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "mobileno":
        setmobileno(value);
        break;
      default:
        break;
    }

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBranchChange = (selectedOption) => {
    setBranch(selectedOption);
    setTouched(prev => ({ ...prev, branch: true }));
    const error = validateField("branch", selectedOption);
    setErrors(prev => ({ ...prev, branch: error }));
  };

  const handleCountryChange = async (selectedOption) => {
    setcountry(selectedOption);
    setstate(null);
    setcity(null);
    setstatecode("");

    try {
      const response = await axios.get(
        BASE_URL + `state/stateByCountry/${selectedOption.value}`,
        { headers: authHeader() }
      );
      setstateOptions(response.data.map(state => ({
        value: state.id,
        label: state.statename,
        code: state.statecode
      })));
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch states. Please try again.',
      });
    }
  };

  const handleStateChange = async (selectedOption) => {
    setstate(selectedOption);
    setcity(null);
    setstatecode(selectedOption.code || "");

    try {
      const response = await axios.get(
        BASE_URL + `city/cityByStateId/${selectedOption.value}`,
        { headers: authHeader() }
      );
      setcityOptions(response.data.map(city => ({
        value: city.id,
        label: city.cityname
      })));
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch cities. Please try again.',
      });
    }
  };

  const checkRequiredFields = () => {
    const requiredFields = [
      { name: 'warehousename', value: warehousename, label: 'Warehouse Name' },
      { name: 'warehousetype', value: warehousetype, label: 'Warehouse Type' },
      { name: 'branch', value: branch, label: 'Branch' }
    ];

    const missingFields = requiredFields.filter(field => {
      if (field.name === 'branch') return !field.value;
      return !field.value.trim();
    });

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(field => field.label).join(', ');
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Fields',
        text: `Please fill in the following fields: ${fieldNames}`,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setTouched({
      warehousename: true,
      warehousetype: true,
      branch: true,
      contactperson: true,
      email: true,
      mobileno: true
    });

    const newErrors = {
      warehousename: validateField('warehousename', warehousename),
      warehousetype: validateField('warehousetype', warehousetype),
      branch: validateField('branch', branch),
      contactperson: validateField('contactperson', contactperson),
      email: validateField('email', email),
      mobileno: validateField('mobileno', mobileno)
    };
    setErrors(newErrors);

    if (!checkRequiredFields() || Object.values(newErrors).some(error => error)) {
      return;
    }

    setIsLoading(true);

    const warehouseData = {
      branch: { id: branch.value },
      warehousename,
      warehousetype,
      contactperson,
      email,
      mobileno,
      address,
      zipcode,
      country: country ? { id: country.value } : null,
      state: state ? { id: state.value } : null,
      city: city ? { id: city.value } : null,
      statecode,
    };

    try {
      await axios.put(`${BASE_URL}warehouse/update/${id}`, warehouseData, {
        headers: authHeader(),
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Warehouse updated successfully!',
      });
      navigate("/warehouseindex/1");
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data || "Failed to update warehouse",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="Container">
      <Card className="card">
        <Card.Header className="card-header">
          <h6>UPDATE WAREHOUSE DETAILS</h6>
          <Link to={`/warehouseindex/${pointindex}`}>
            <button className="button-70">BACK</button>
          </Link>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md="6">
                <Form.Group controlId="formwarehousename">
                  <Form.Label>Warehouse Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="warehousename"
                    placeholder="Enter warehouse name"
                    value={warehousename}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.warehousename && !!errors.warehousename}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.warehousename}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formwarehousetype">
                  <Form.Label>Warehouse Type<span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="warehousetype"
                    value={warehousetype}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.warehousetype && !!errors.warehousetype}
                  >
                    <option value="">Select warehouse Type</option>
                    <option value="Primary">Primary</option>
                    <option value="Secondary">Secondary</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.warehousetype}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="supplier">
                  <Form.Label>Branch<span className="text-danger">*</span></Form.Label>
                  <Select
                    value={branch}
                    onChange={handleBranchChange}
                    onBlur={() => setTouched(prev => ({ ...prev, branch: true }))}
                    options={branchOptions}
                    placeholder="Select branch"
                    className={touched.branch && errors.branch ? 'is-invalid' : ''}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                  {touched.branch && errors.branch && (
                    <div className="invalid-feedback d-block">
                      {errors.branch}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcontactperson">
                  <Form.Label>Contact Person</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactperson"
                    value={contactperson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.contactperson && !!errors.contactperson}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactperson}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formemail">
                  <Form.Label>Email ID</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formmobileno">
                  <Form.Label>Phone No.</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobileno"
                    value={mobileno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileno && !!errors.mobileno}
                    maxLength="10"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileno}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md="12">
                <Form.Group controlId="formaddress">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={address}
                    onChange={(e) => setaddress(e.target.value)}
                    placeholder="Enter Address ..... "
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcountry">
                  <Form.Label>Country</Form.Label>
                  <Select
                    value={country}
                    onChange={handleCountryChange}
                    options={countryOptions}
                    placeholder="Select country"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formstate">
                  <Form.Label>State</Form.Label>
                  <Select
                    value={state}
                    onChange={handleStateChange}
                    options={stateOptions}
                    placeholder="Select state"
                    isDisabled={!country}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formstatecode">
                  <Form.Label>State Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={statecode}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formcity">
                  <Form.Label>City</Form.Label>
                  <Select
                    value={city}
                    onChange={(selectedOption) => setcity(selectedOption)}
                    options={cityOptions}
                    placeholder="Select city"
                    isDisabled={!state}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group controlId="formzipcode">
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="number"
                    value={zipcode}
                    onChange={(e) => setzipcode(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <button
              className="btn btn-success"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : "UPDATE"}
            </button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default WarehouseEdit;


